import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import type { MouseEvent } from 'react';

import { CONTAINER, CTA_UTILS, EM_ACCENT, EYEBROW, EYEBROW_MARK } from '../classes';
import { CountFigure, useCountProgress } from '../count-up';
import { DigitalRain } from '../digital-rain';
import { FORM_ID, IMG, m } from '../shared';

const STATS: Array<{ value: string; label: string }> = [
  { value: 'lp.closing.lessPing', label: 'lp.closing.lessPingLabel' },
  { value: 'lp.closing.moreFps', label: 'lp.closing.moreFpsLabel' },
  { value: 'lp.closing.servers', label: 'lp.closing.serversLabel' },
  { value: 'lp.closing.countries', label: 'lp.closing.countriesLabel' },
];

export function Closing({ onTrialClick }: { onTrialClick: (event: MouseEvent<HTMLAnchorElement>) => void }) {
  const { t } = useTranslation('pages/trial-matrix');
  const { locale } = useRouter();
  const [statsRef, progress] = useCountProgress<HTMLUListElement>(1600);

  return (
    <section className='relative isolate scroll-mt-6 overflow-hidden border-t border-[#93FF18]/10 bg-[linear-gradient(180deg,#070A08,#060B07)] py-[clamp(3.5rem,2rem+5vw,7rem)]' id='fechamento' aria-labelledby='end-t'>
      <DigitalRain variant='end' />
      <div className={`${CONTAINER} relative z-[2] grid justify-items-center text-center`}>
        <p className={`${EYEBROW} justify-center`}>
          <span className={EYEBROW_MARK} aria-hidden='true'>
            &gt;
          </span>{' '}
          {t('lp.closing.eyebrow')}
        </p>
        <h2 className={`max-w-[18em] text-balance text-[min(clamp(1.875rem,1.2rem+2.7vw,3.75rem),10vw)] font-bold leading-[1.03] tracking-[-.035em] text-white ${EM_ACCENT} [&_em]:[text-shadow:0_0_34px_rgb(147_255_24/.45)]`} id='end-t'>
          <Trans t={t} i18nKey='lp.closing.title' components={{ em: <em /> }} />
        </h2>
        <p className='mt-5 max-w-[36em] text-[length:var(--fs-lg)] leading-[1.6] text-[#CBD5E1]'>{t('lp.closing.lead')}</p>
        <a className={`${m('btn-primary cta')} ${CTA_UTILS} mt-[clamp(1.75rem,1rem+2vw,2.5rem)] !min-h-[3.75rem] !px-10 max-sm:!min-h-14 max-sm:!px-6`} href={`#${FORM_ID}`} onClick={onTrialClick}>
          {t('lp.cta')}
        </a>
        <ul ref={statsRef} className='mt-[clamp(2rem,1.2rem+2.4vw,3rem)] grid w-full max-w-[52rem] grid-cols-[repeat(auto-fit,minmax(min(9rem,100%),1fr))] gap-x-6 gap-y-4'>
          {STATS.map((stat) => (
            <li key={stat.value} className='flex min-w-0 flex-col gap-[.2rem] border-t border-[#2C3B31] pt-3'>
              <strong className='whitespace-nowrap text-[min(clamp(1.375rem,1rem+1.4vw,2rem),9vw)] font-bold tabular-nums leading-[1.05] tracking-[-.035em] text-[#93FF18]'>
                <Trans t={t} i18nKey={stat.value} components={{ n: <CountFigure progress={progress} locale={locale} grouped className='tabular-nums' valueClassName='justify-self-end' /> }} />
              </strong>
              <span className='text-[.75rem] leading-[1.3333] text-[#9AA3B5]'>{t(stat.label)}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function MatrixFooter() {
  const { t } = useTranslation('pages/trial-matrix');

  return (
    <footer className="relative z-[1] bg-[#06080A] pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))] pt-10 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-[linear-gradient(90deg,rgb(255_255_255/.04),rgb(255_255_255/.16)_20%,rgb(147_255_24/.35)_50%,rgb(255_255_255/.16)_80%,rgb(255_255_255/.04))] before:content-['']">
      <div className='mx-auto flex max-w-[90rem] flex-col items-start gap-3 px-[max(var(--gutter),env(safe-area-inset-left,0px),env(safe-area-inset-right,0px))] sm:flex-row sm:items-center sm:justify-between sm:gap-8'>
        <Link className='-ms-2 inline-flex min-h-11 items-center rounded-lg px-2 transition-opacity duration-[160ms] hover:opacity-[.85]' href='/' aria-label={t('lp.footer.home')}>
          <picture>
            <source type='image/avif' srcSet={`${IMG}/logo-256.avif 256w, ${IMG}/logo-720.avif 720w`} sizes='(min-width: 64em) 120px, (min-width: 40em) 100px, 84px' />
            <img className='h-auto w-[84px] sm:w-[100px] lg:w-[120px]' src={`${IMG}/logo-256.webp`} srcSet={`${IMG}/logo-256.webp 256w, ${IMG}/logo-720.webp 720w`} sizes='(min-width: 64em) 120px, (min-width: 40em) 100px, 84px' alt='' width={720} height={166} loading='lazy' decoding='async' />
          </picture>
        </Link>
        <p className='text-[.875rem] leading-[1.4286] tracking-[.02em] text-[#9AA3B5]'>{t('lp.footer.copyright', { year: new Date().getFullYear() })}</p>
      </div>
      <p className='mx-auto mt-4 px-[var(--gutter)] text-center text-[.75rem] leading-[1.3333] text-[#F4F6F7] opacity-[.65]'>{t('lp.footer.note')}</p>
    </footer>
  );
}
